import React, { useState } from "react";
import { UploadCloudIcon, XIcon } from "lucide-react";

import { cn } from "@/libs/cn";

const MAX_VISIBLE_FILES = 5;

interface FileInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "value" | "onChange"> {
  files: File[];
  onChange: (files: File[]) => void;
  title?: string;
  description?: string;
}

const FileInput = ({
  files,
  onChange,
  title = "Clique para anexar um arquivo",
  description = "Ou arraste até aqui",
  accept,
  multiple,
  disabled,
  className,
  ...props
}: FileInputProps) => {
  const [showAllFiles, setShowAllFiles] = useState(false);

  const isValidFile = (file: File) => {
    if (!accept) return true;

    return accept
      .split(",")
      .map((type) => type.trim().toLowerCase())
      .some((type) => {
        if (type.startsWith(".")) return file.name.toLowerCase().endsWith(type);
        if (type.endsWith("/*")) return file.type.toLowerCase().startsWith(type.slice(0, -1));
        return file.type.toLowerCase() === type;
      });
  };

  const processFiles = (newFiles: File[]) => {
    const validFiles = newFiles.filter(isValidFile);
    if (!validFiles.length) return;

    onChange(multiple ? [...files, ...validFiles] : [validFiles[0]]);
  };

  const removeFile = (index: number) => {
    const updatedFiles = files.filter((_, fileIndex) => fileIndex !== index);
    onChange(updatedFiles);

    if (updatedFiles.length <= MAX_VISIBLE_FILES) setShowAllFiles(false);
  };

  const visibleFiles = showAllFiles ? files : files.slice(0, MAX_VISIBLE_FILES);

  return (
    <div className={cn("flex w-full flex-col gap-2", className)}>
      <div
        className={cn(
          "relative flex flex-col items-center gap-2 rounded-lg bg-ui-hover p-4",
          disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
        )}
        onDrop={(event) => {
          event.preventDefault();
          if (!disabled) processFiles(Array.from(event.dataTransfer.files));
        }}
        onDragOver={(event) => event.preventDefault()}
      >
        <input
          type="file"
          title=" "
          accept={accept}
          multiple={multiple}
          disabled={disabled}
          className="absolute left-0 top-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
          onChange={(event) => {
            processFiles(Array.from(event.target.files ?? []));
            event.target.value = "";
          }}
          {...props}
        />
        <p className="text-xs font-bold text-ui-text">{title}</p>
        <UploadCloudIcon className="h-8 w-8 text-ui-primary" />
        <p className="text-xs text-ui-text-muted">{description}</p>
      </div>

      {files.length > 0 && (
        <div className="flex max-h-41 flex-col overflow-y-auto p-2">
          {visibleFiles.map((file, index) => (
            <div className="flex items-center gap-2" key={`${file.name}-${index}`}>
              <button
                type="button"
                aria-label="Remover arquivo"
                disabled={disabled}
                className="h-6 w-6 shrink-0 cursor-pointer rounded-full p-1 text-red-500 hover:bg-red-100 disabled:cursor-not-allowed"
                onClick={() => removeFile(index)}
              >
                <XIcon className="h-4 w-4" />
              </button>
              <p className="min-w-0 truncate text-sm text-ui-text">{file.name}</p>
            </div>
          ))}

          {files.length > MAX_VISIBLE_FILES && !showAllFiles && (
            <button type="button" className="cursor-pointer text-left text-sm text-ui-primary" onClick={() => setShowAllFiles(true)}>
              {`+${files.length - MAX_VISIBLE_FILES} arquivos`}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default FileInput;
